import { api } from "@/app/services/api.service";

const GATEWAY_BASE = "configuration/gateway";

export const MAX_UPLOAD_SIZE_PATH = `${GATEWAY_BASE}/max-upload-size`;
export const BLOCKED_REQUESTS_PATH = `${GATEWAY_BASE}/blocked-requests`;
export const EXEMPT_KEYWORDS_PATH = `${GATEWAY_BASE}/exempt-keywords`;
export const EXEMPT_KEYWORDS_AUDIT_PATH = `${GATEWAY_BASE}/exempt-keywords/audit`;

export function unwrapPayload(res: { data: unknown }) {
  const data = res.data as Record<string, unknown>;
  if (
    data != null &&
    typeof data === "object" &&
    Object.prototype.hasOwnProperty.call(data, "data") &&
    data.data != null
  ) {
    return data.data;
  }
  return data;
}

export function extractErrorMessage(error: unknown, fallback: string) {
  const err = error as { response?: { data?: { message?: string } } };
  return err.response?.data?.message || fallback;
}

export type MaxUploadSizeConfig = {
  mb: number | null;
  bytes: number | null;
  source: string | null;
};

export function parseMaxUploadSize(body: unknown): MaxUploadSizeConfig {
  if (body == null || typeof body !== "object") {
    return { mb: null, bytes: null, source: null };
  }
  const record = body as Record<string, unknown>;
  let mb: number | null = null;
  if (record.maxUploadSizeInMB != null && !Number.isNaN(Number(record.maxUploadSizeInMB))) {
    mb = Number(record.maxUploadSizeInMB);
  } else if (record.sizeInMB != null && !Number.isNaN(Number(record.sizeInMB))) {
    mb = Number(record.sizeInMB);
  } else if (record.sizeInMb != null && !Number.isNaN(Number(record.sizeInMb))) {
    mb = Number(record.sizeInMb);
  }

  let bytes: number | null = null;
  if (record.maxUploadSizeInBytes != null && !Number.isNaN(Number(record.maxUploadSizeInBytes))) {
    bytes = Number(record.maxUploadSizeInBytes);
  } else if (record.sizeInBytes != null && !Number.isNaN(Number(record.sizeInBytes))) {
    bytes = Number(record.sizeInBytes);
  }

  const source = record.source != null ? String(record.source) : null;
  return { mb, bytes, source };
}

export async function fetchMaxUploadSize(): Promise<MaxUploadSizeConfig> {
  const res = await api().get(MAX_UPLOAD_SIZE_PATH);
  const body = unwrapPayload(res);
  return parseMaxUploadSize(body);
}

export async function saveMaxUploadSize(sizeInMB: number) {
  await api().post(MAX_UPLOAD_SIZE_PATH, { sizeInMB });
}

export function extractBlockedList(body: unknown): Record<string, unknown>[] {
  if (body == null) return [];
  if (Array.isArray(body)) {
    return body.filter((item) => item != null && typeof item === "object") as Record<string, unknown>[];
  }
  const obj = body as Record<string, unknown>;
  const candidates = [obj.blockedRequests, obj.items, obj.content, obj.records, obj.data];
  for (const candidate of candidates) {
    const parsed = extractBlockedList(candidate);
    if (parsed.length > 0) return parsed;
  }
  return [];
}

export async function fetchBlockedRequests(): Promise<Record<string, unknown>[]> {
  const res = await api().get(BLOCKED_REQUESTS_PATH);
  const root = res.data;
  if (Array.isArray(root)) return extractBlockedList(root);
  if (root != null && typeof root === "object") {
    const rootObj = root as Record<string, unknown>;
    if (Array.isArray(rootObj.data)) return extractBlockedList(rootObj.data);
    const unwrapped = unwrapPayload(res);
    const fromUnwrapped = extractBlockedList(unwrapped);
    if (fromUnwrapped.length > 0) return fromUnwrapped;
    return extractBlockedList(root);
  }
  return [];
}

export function extractKeywordList(body: unknown): string[] {
  if (body == null) return [];
  if (Array.isArray(body)) {
    return body
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item != null && typeof item === "object") {
          const record = item as Record<string, unknown>;
          if (typeof record.keyword === "string") return record.keyword.trim();
        }
        return "";
      })
      .filter((keyword) => keyword.length > 0);
  }
  const obj = body as Record<string, unknown>;
  const candidates = [obj.keywords, obj.exemptKeywords, obj.items, obj.content, obj.data];
  for (const candidate of candidates) {
    const parsed = extractKeywordList(candidate);
    if (parsed.length > 0) return parsed;
  }
  return [];
}

export async function fetchExemptKeywords(): Promise<string[]> {
  const res = await api().get(EXEMPT_KEYWORDS_PATH);
  const root = res.data;
  if (Array.isArray(root)) return extractKeywordList(root);
  if (root != null && typeof root === "object") {
    const rootObj = root as Record<string, unknown>;
    if (Array.isArray(rootObj.data)) return extractKeywordList(rootObj.data);
    const unwrapped = unwrapPayload(res);
    const fromUnwrapped = extractKeywordList(unwrapped);
    if (fromUnwrapped.length > 0) return fromUnwrapped;
    return extractKeywordList(root);
  }
  return [];
}

export async function addExemptKeyword(keyword: string) {
  await api().post(EXEMPT_KEYWORDS_PATH, { keyword });
}

export async function removeExemptKeyword(keyword: string) {
  await api().delete(EXEMPT_KEYWORDS_PATH, { data: { keyword } });
}

export function extractAuditList(body: unknown): Record<string, unknown>[] {
  if (body == null) return [];
  if (Array.isArray(body)) {
    return body.filter((item) => item != null && typeof item === "object") as Record<string, unknown>[];
  }
  const obj = body as Record<string, unknown>;
  const candidates = [obj.auditLogs, obj.logs, obj.items, obj.content, obj.records, obj.data];
  for (const candidate of candidates) {
    const parsed = extractAuditList(candidate);
    if (parsed.length > 0) return parsed;
  }
  return [];
}

export async function fetchExemptKeywordsAudit(): Promise<Record<string, unknown>[]> {
  const res = await api().get(EXEMPT_KEYWORDS_AUDIT_PATH);
  const root = res.data;
  if (Array.isArray(root)) return extractAuditList(root);
  if (root != null && typeof root === "object") {
    const rootObj = root as Record<string, unknown>;
    if (Array.isArray(rootObj.data)) return extractAuditList(rootObj.data);
    const unwrapped = unwrapPayload(res);
    const fromUnwrapped = extractAuditList(unwrapped);
    if (fromUnwrapped.length > 0) return fromUnwrapped;
    return extractAuditList(root);
  }
  return [];
}
